import { BaseElementDef, BaseElementDefBase } from "./BaseElementDef";
import { ForEachSettings } from "@cs-ts/for-each-settings";
import { ForEachChildIdSettings } from "@cs-ts/for-each-child-id-settings";
import { ForEachChildIndexSettings } from "@cs-ts/for-each-child-index-settings";
import { IElementSettings } from "@cs-ts/i-element-settings";
import { FormItem } from "@cs/FormItem";
import { FormControlItem } from "@cs/FormControlItem";
import { FormForEachIdItem } from "@cs/FormForEachIdItem";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { CalculationContext } from "./__CalculationContextInterface";
import { combineLatest, from, BehaviorSubject } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { Observable } from "rxjs";

export default class ForEachDef extends BaseElementDef<ForEachSettings, FormDefinitionItem<ForEachSettings>, FormItem> {
    //declare valueId : string;
    //declare fileName : string;
    //declare fileSize : string;
    //declare contentType : string;
    //declare fileUrl : string;
    //declare isFileUpload : boolean;
    //declare uploadStatus : string|null;
    declare _effectiveLabel$: BehaviorSubject<string | null>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<ForEachSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFForEach"; // TODO - not hardcoded?
    }

    initChildren(data: FormItem) {
        this.calculationContext.rootElement!.dependenciesIndex = this.calculationContext.rootElement!.dependenciesIndex || [];
        const dependenciesIndex = this.calculationContext.rootElement!.dependenciesIndex;
        const topIndex = dependenciesIndex.length > 0 ? dependenciesIndex[0] : 0;
        dependenciesIndex.unshift(0);
        if (data == null) {
            const childCounts = this.calculationContext.rootElement!.foreachCounts[this.id!];
            const childrenCount = childCounts == null ? this.definition.settings.min || 0 : childCounts[topIndex];
            for (let i = 0; i < topIndex; i++) {
                dependenciesIndex[0] += childCounts[i];
            }
            this.children = new Array(childrenCount + 0).fill(0).map(index => {
                const r = this.generateItem(null);
                dependenciesIndex[0]++;
                return r;
            });
        } else {
            this.children =
                !data || !data.children
                    ? []
                    : data.children.map(child => {
                          const r = this.generateItem(child as FormForEachIdItem);
                          dependenciesIndex[0]++;
                          return r;
                      });
            while (this.children.length < (this.definition.settings.min || 0)) {
                this.addItem(null);
            }
        }
        dependenciesIndex.shift();
        this._children$.next(this.children);
    }

    setData(data: FormItem | null, excludeChildren?: boolean) {
        super.setData(data, true);
        if (!excludeChildren) {
            this.calculationContext.rootElement!.dependenciesIndex = this.calculationContext.rootElement!.dependenciesIndex || [];
            const dependenciesIndex = this.calculationContext.rootElement!.dependenciesIndex;
            const topIndex = dependenciesIndex.length > 0 ? dependenciesIndex[0] : 0;
            dependenciesIndex.unshift(0);
            let childrenCount;
            if (data != null) {
                childrenCount = data.children.length;
            } else {
                if (this.id! in this.calculationContext.rootElement!.foreachCounts) {
                    const childCounts = this.calculationContext.rootElement!.foreachCounts[this.id!];
                    for (let i = 0; i < topIndex; i++) {
                        dependenciesIndex[0] += childCounts[i];
                    }
                    childrenCount = childCounts[topIndex];
                } else {
                    childrenCount = this.children ? this.children.length : this.definition.settings.min || 0;
                }
            }
            const children = (this.children ? this.children.slice(0, childrenCount) : []) as ForEachDefItem[];
            for (let index = 0; index < childrenCount; index++) {
                const child = data != null ? (data.children[index] as FormForEachIdItem) : null;
                if (children[index]) {
                    children[index].setData(child);
                } else {
                    children[index] = this.generateItem(child);
                }
                dependenciesIndex[0]++;
            }
            dependenciesIndex.shift();
            this.setItems(children);
        }
    }

    setCalculationItems(ids: string[]) {
        let manualCounter = 0;
        let children = (this.children as ForEachDefItem[]).filter(i => {
            if (i.isManualChild) manualCounter++;
            return ids.includes(i.childId!) || i.isManualChild;
        });
        if (children.length == ids.length + manualCounter && this.children.length == ids.length + manualCounter) {
            return;
        }
        const idsToAdd = ids.filter(i => !children.some(c => c.childId == i));
        if (idsToAdd.length > 0) {
            idsToAdd.forEach(id => {
                const visibleChildrenLength = children.filter(child => !child.removedChild).length;
                children.push(this.generateItem({ childId: id, childIndex: visibleChildrenLength + 1, isManualChild: false } as FormForEachIdItem));
            });
        } else {
            children
                .filter(child => !child.removedChild)
                .forEach((child, index) => {
                    child.childIndex = (index + 1).toString();
                    child.childIndex$.next(child.childIndex);
                });
        }
        children = ids.map(i => children.filter(j => j.childId == i)[0]).sort((a, b) => (a.childIndex == null ? 1 : b.childIndex == null ? -1 : parseInt(a.childIndex) - parseInt(b.childIndex)));
        if (this.definition.settings.addRemoveItems) {
            const manuallyAddedChildren = (this.children as ForEachDefItem[]).filter(i => i.isManualChild == true);
            manuallyAddedChildren.forEach(manualChild => {
                children.splice(parseInt(manualChild.childIndex!) - 1, 0, manualChild);
            });
        }
        //console.log(`Setting children: ${this.definition.id}|${this.vmId}:\n    ${newIds}\n => ${this.data.children.map(i => i.childId)}`)
        this.setItems(children);
    }

    setItems(items: ForEachDefItem[]) {
        this.children = items;
        this._children$.next(items);
        if (this.baseLink != null && !this.isBase) {
            (this.baseLink as ForEachDef).setItems(items.map(i => i.baseLink as ForEachDefItem));
        }
    }

    generateItem(data: FormForEachIdItem | null) {
        const item = new ForEachDefItem(this, data, this.definition, this.calculationContext);
        if (this.baseLink != null && !this.isBase) {
            const baseElement = (this.baseLink as ForEachDef).generateItem(data);
            item.linkBaseElement(baseElement);
        }
        return item;
    }

    addItem(data: FormForEachIdItem | null) {
        const item = this.generateItem(data || ({} as FormForEachIdItem));
        if (item.childId == null) {
            item.isManualChild = true;
        }
        this._addItem(item);
    }

    _addItem(item: ForEachDefItem) {
        if (item.childIndex == null) {
            item.childIndex = ((this.children as ForEachDefItem[]).filter(c => !c.removedChild).length + 1).toString();
            item.childIndex$.next(item.childIndex);
        }
        this.children.push(item);
        this._children$.next(this.children);
        if (this.baseLink != null && item.baseLink != null && !this.isBase) {
            (this.baseLink as ForEachDef)._addItem(item.baseLink as ForEachDefItem);
        }
    }

    removeItem(item: ForEachDefItem, calculatedItems: string[]) {
        const index = this.children.indexOf(item);

        if (index >= 0) {
            if (!item.isManualChild && calculatedItems.length > 0 && calculatedItems.filter(x => x.includes(item.childId!))) {
                const childItem = (this.children as ForEachDefItem[])[index];
                childItem.removedChild = true;
                childItem.childIndex = null;
            } else {
                this.children.splice(index, 1);
            }
            for (let i = index; i < this.children.length; i++) {
                const child = (this.children as ForEachDefItem[])[i];
                child.childIndex = child.childIndex == null ? null : (parseInt(child.childIndex!) - 1).toString();
                child.childIndex$.next(child.childIndex);
            }
            this._children$.next(this.children);
        }
        if (this.baseLink != null && item.baseLink != null && !this.isBase) {
            (this.baseLink as ForEachDef).removeItem(item.baseLink as ForEachDefItem, []);
        }
    }

    valid$(includeUnedited: boolean, excludeChildren?: boolean) {
        const valid$ = super.valid$(includeUnedited, excludeChildren);
        const thisValid$ = this.errors$().pipe(map(i => i.length == 0));
        const result = combineLatest(valid$, thisValid$).pipe(
            //tap(i => { debugger; }),
            map(([superValid, thisValid]) => superValid && thisValid),
        );
        return result;
    }

    errors$() {
        return this._children$.pipe(
            map(children => {
                const result = [];
                if (this.definition.settings.min != null && this.children.length < this.definition.settings.min) {
                    result.push(`Must have at least ${this.definition.settings.min} ${this.getLabel()}`);
                }
                if (this.definition.settings.max != null && this.children.length > this.definition.settings.max) {
                    result.push(`Must have at most ${this.definition.settings.max} ${this.getLabel()}`);
                }
                return result;
            }),
        );
    }

    _getElementsById$(id: string, contextVmId: string): (BaseElementDef<IElementSettings, FormDefinitionItem<IElementSettings>, FormItem> | Observable<BaseElementDefBase[]>)[] {
        if (this.id == id) {
            return [this];
        }
        const walk = (node: FormDefinitionItem<IElementSettings>): boolean => {
            return node.children.some(child => {
                if (child.id == id) {
                    return true;
                }
                return walk(child);
            });
        };
        const idInScope = walk(this.definition);
        if (!idInScope) {
            return super._getElementsById$(id, contextVmId);
        }
        let context2 = null as BaseElementDefBase | null;
        this.children.some(child => {
            if (child.getElementByVmId(contextVmId)) {
                context2 = child;
            }
            return context2 != null;
        });
        if (context2 != null) {
            return context2._getElementsById$(id, contextVmId);
        }
        return [
            this._children$.pipe(
                switchMap(list => {
                    let context = null as BaseElementDefBase | null;
                    list.some(child => {
                        if (child.getElementByVmId(contextVmId)) {
                            context = child;
                        }
                        return context != null;
                    });
                    if (context != null) {
                        debugger;
                    }
                    const unwrappedResults = context != null ? context._getElementsById$(id, contextVmId) : super._getElementsById$(id, contextVmId);
                    const observableResults = unwrappedResults.filter(i => "pipe" in i) as Observable<BaseElementDefBase[]>[];
                    const nonObservableResults = unwrappedResults.filter(i => !("pipe" in i)) as BaseElementDefBase[];
                    if (nonObservableResults.length > 0) {
                        observableResults.push(from([nonObservableResults]));
                    }
                    return (observableResults.length == 0 ? from([[]]) : combineLatest(observableResults).pipe(map(i => flattenArrays(i).filter(j => !(j.parentElement as ForEachDefItem).removedChild)))) as Observable<BaseElementDefBase[]>;
                }),
            ),
        ];
    }

    // TODO duplicate of BaseControlDef
    effectiveLabel$() {
        if (this._effectiveLabel$ == null) {
            if (this.definition.settings.labelCalculationId) {
                this._effectiveLabel$ = new BehaviorSubject(null as string | null);
                this.resolveCalculation(this.definition.settings.labelCalculationId)
                    .pipe(map(i => (i.length ? i[0] : null)))
                    .subscribe(this._effectiveLabel$);
            } else {
                this._effectiveLabel$ = new BehaviorSubject(this.definition.settings.label as string | null);
            }
        }
        return this._effectiveLabel$;
    }
}

function flattenArrays<T>(arrays: T[][]): T[] {
    return Array.prototype.concat.apply([], arrays);
}

export class ForEachDefItem extends BaseElementDef<ForEachSettings, FormDefinitionItem<ForEachSettings>, FormItem> {
    declare childId: string | null | undefined;
    declare removedChild: boolean;
    declare isManualChild: boolean;
    declare childIndex: string | null | undefined;
    declare childIndex$: BehaviorSubject<string | null | undefined>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem | null, definitionData: FormDefinitionItem<ForEachSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData!, definitionData, calculationContext);
        this.typeName = null!; // "MI.Common.DynamicForms.Models.DFForEach+Child"; // TODO - not hardcoded?
        this.childIndex$ = new BehaviorSubject(this.childIndex);
    }

    /** @returns {Observable<string[]>} */
    getVirtualContext$() {
        if (this.parentElement == null) {
            return from([[]]);
        } else {
            return this.parentElement.getVirtualContext$().pipe(switchMap((j: string[]) => this.parentElement!._children$.pipe(map((i: BaseElementDefBase[]) => [...j, i.indexOf(this).toString()]))));
        }
    }

    initChildren(data: FormForEachIdItem) {
        super.initChildren(data);
        if (data != null) {
            if (data.childId == undefined && this.definition.settings.addRemoveItems) {
                this.childId = null;
            } else {
                this.childId = data.childId;
            }
            this.removedChild = data.removedChild;
            this.isManualChild = data.isManualChild;
            this.childIndex = data.childIndex?.toString();
        }
        this.children.filter(i => i instanceof ForEachChildIdDef || i instanceof ForEachChildIndexDef).forEach(c => (c as ForEachChildIdDef | ForEachChildIndexDef).setForEachItem(this));
    }
}

export class ForEachChildIdDef extends BaseElementDef<ForEachChildIdSettings, FormDefinitionItem<ForEachChildIdSettings>, FormControlItem> {
    declare foreachItem: ForEachDefItem | null;
    declare foreachItem$: BehaviorSubject<ForEachDefItem | null>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormControlItem, definitionData: FormDefinitionItem<ForEachChildIdSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFForEach+ChildId"; // TODO - not hardcoded?
        this.foreachItem = null;
        this.foreachItem$ = new BehaviorSubject(null as ForEachDefItem | null);
    }

    setData(data: FormControlItem | null, excludeChildren?: boolean) {
        super.setData(data, excludeChildren);
    }
    getData(excludeChildren?: boolean) {
        const result = super.getData(excludeChildren) as FormControlItem;
        if (result != null) {
            result.value = this.childId();
            result.removedChild = this.removedChild();
            result.isManualChild = this.manualChild();
        }
        return result;
    }

    setForEachItem(foreachItem: ForEachDefItem) {
        this.foreachItem = foreachItem;
        this.foreachItem$.next(foreachItem);
    }

    //value() {
    //    return this.childId();
    //}

    valueList$() {
        return this.foreachItem$.pipe(map(i => (i == null ? [] : [i.childId]))) as Observable<string[]>;
    }

    effectiveValueList$() {
        return this.valueList$();
    }

    childId() {
        return this.foreachItem!.childId!;
    }
    removedChild() {
        return this.foreachItem!.removedChild!;
    }
    manualChild() {
        return this.foreachItem!.isManualChild!;
    }
}

export class ForEachChildIndexDef extends BaseElementDef<ForEachChildIndexSettings, FormDefinitionItem<ForEachChildIndexSettings>, FormControlItem> {
    declare foreachItem: ForEachDefItem | null;
    declare foreachItem$: BehaviorSubject<ForEachDefItem | null>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormControlItem, definitionData: FormDefinitionItem<ForEachChildIndexSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFForEach+ChildIndex"; // TODO - not hardcoded?
        this.foreachItem = null;
        this.foreachItem$ = new BehaviorSubject(null as ForEachDefItem | null);
    }

    setData(data: FormControlItem | null, excludeChildren?: boolean) {
        super.setData(data, excludeChildren);
    }
    getData(excludeChildren?: boolean) {
        const result = super.getData(excludeChildren) as FormControlItem;
        if (result != null) {
            result.value = this.childIndex();
        }
        return result;
    }

    setForEachItem(foreachItem: ForEachDefItem) {
        this.foreachItem = foreachItem;
        this.foreachItem$.next(foreachItem);
    }

    //value() {
    //    return this.childIndex();
    //}

    valueList$() {
        return this.foreachItem$.pipe(switchMap(i => (i == null ? [] : i.childIndex$.pipe(map(j => (j == null ? [] : [j]))))));
    }

    effectiveValueList$() {
        return this.valueList$();
    }

    childIndex() {
        return this.foreachItem!.childIndex!;
    }
}
